import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProfile, updateProfile, changePassword, getUserStats, closeAccount, purgeUserData, getParametres } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { invalidateCache } from '../utils/cache'
import Avatar from '../components/Avatar'
import Icon from '../components/Icon'
import { Amount } from '../utils/formatMoney'
import { useToast } from '../components/ToastProvider'
import { useConfirm } from '../components/ConfirmProvider'
import { useTheme } from '../hooks/useTheme'

export default function Profil() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()
  const { setTheme } = useTheme()
  const [profile, setProfile] = useState(null)
  const [parametres, setParametres] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('profile')
  const [message, setMessage] = useState({ type: '', text: '' })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [closingAccount, setClosingAccount] = useState(false)
  const [purgingData, setPurgingData] = useState(false)

  // Formulaire de profil
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    // Informations personnelles
    prenom: '',
    nom: '',
    telephone: '',
    date_naissance: '',
    adresse_rue: '',
    adresse_ville: '',
    adresse_code_postal: '',
    adresse_pays: '',
    aerobase: '',
    avatar: '',
    // Préférences
    langue: 'fr',
    fuseau_horaire: 'Europe/Paris',
    format_date: 'DD/MM/YYYY',
    format_heure: '24h',
    notifications_email: true,
    notifications_push: true,
    notifications_alertes: true,
    theme_prefere: 'auto'
  })

  // Formulaire de mot de passe
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  })

  useEffect(() => {
    loadProfile()
  }, [])

  // Écouter les changements de token pour recharger le profil
  useEffect(() => {
    const handleTokenChange = () => {
      // Recharger le profil avec forceRefresh quand le token change (nouvelle connexion/inscription)
      loadProfile(true)
    }
    const handleUserLoggedOut = () => {
      // Réinitialiser le profil quand l'utilisateur se déconnecte
      setProfile(null)
      setStats(null)
      setProfileForm({
        username: '',
        email: '',
        prenom: '',
        nom: '',
        telephone: '',
        date_naissance: '',
        adresse_rue: '',
        adresse_ville: '',
        adresse_code_postal: '',
        adresse_pays: '',
        aerobase: '',
        avatar: '',
        langue: 'fr',
        fuseau_horaire: 'Europe/Paris',
        format_date: 'DD/MM/YYYY',
        format_heure: '24h',
        notifications_email: true,
        notifications_push: true,
        notifications_alertes: true,
        theme_prefere: 'auto'
      })
    }
    window.addEventListener('tokenChanged', handleTokenChange)
    window.addEventListener('userLoggedOut', handleUserLoggedOut)
    return () => {
      window.removeEventListener('tokenChanged', handleTokenChange)
      window.removeEventListener('userLoggedOut', handleUserLoggedOut)
    }
  }, [])

  const loadProfile = async (forceRefresh = false) => {
    try {
      setLoading(true)
      // Invalider le cache si forceRefresh
      if (forceRefresh) {
        invalidateCache('/auth/profile')
        invalidateCache('/auth/me')
      }
      // Les statistiques affichaient un « EUR » fixe : la devise reelle
      // vient des parametres, chargee en parallele du profil.
      const [response, params] = await Promise.all([
        getProfile(forceRefresh),
        getParametres(forceRefresh).catch(() => null),
      ])
      if (params) setParametres(params)
      if (response.success) {
        setProfile(response.user)
        setStats(response.stats)
        setProfileForm({
          username: response.user.username || '',
          email: response.user.email || '',
          // Informations personnelles
          prenom: response.user.prenom || '',
          nom: response.user.nom || '',
          telephone: response.user.telephone || '',
          date_naissance: response.user.date_naissance || '',
          adresse_rue: response.user.adresse_rue || '',
          adresse_ville: response.user.adresse_ville || '',
          adresse_code_postal: response.user.adresse_code_postal || '',
          adresse_pays: response.user.adresse_pays || '',
          aerobase: response.user.aerobase || '',
          avatar: response.user.avatar || '',
          // Préférences
          langue: response.user.langue || 'fr',
          fuseau_horaire: response.user.fuseau_horaire || 'Europe/Paris',
          format_date: response.user.format_date || 'DD/MM/YYYY',
          format_heure: response.user.format_heure || '24h',
          notifications_email: response.user.notifications_email !== undefined ? response.user.notifications_email : true,
          notifications_push: response.user.notifications_push !== undefined ? response.user.notifications_push : true,
          notifications_alertes: response.user.notifications_alertes !== undefined ? response.user.notifications_alertes : true,
          theme_prefere: response.user.theme_prefere || 'auto'
        })
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erreur lors du chargement du profil' })
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    try {
      setMessage({ type: '', text: '' })
      
      // Préparer les données à envoyer
      const dataToSend = { ...profileForm }
      
      // Convertir les chaînes vides en null pour les champs optionnels
      const optionalFields = ['prenom', 'nom', 'telephone', 'date_naissance', 'adresse_rue', 'adresse_ville', 'adresse_code_postal', 'adresse_pays', 'aerobase', 'avatar']
      optionalFields.forEach(field => {
        if (dataToSend[field] === '') {
          dataToSend[field] = null
        }
      })
      
      // S'assurer que format_date a une valeur valide
      const validDateFormats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']
      if (!dataToSend.format_date || !validDateFormats.includes(dataToSend.format_date)) {
        dataToSend.format_date = 'DD/MM/YYYY'
      }
      
      const response = await updateProfile(dataToSend)
      if (response.success) {
        setMessage({ type: 'success', text: 'Profil mis à jour avec succès' })
        setProfile(response.user)
        // Déclencher un événement pour notifier les autres composants (Header, etc.)
        window.dispatchEvent(new Event('profileUpdated'))
        // Recharger le profil pour avoir les dernières données
        await loadProfile()
      } else {
        setMessage({ 
          type: 'error', 
          text: response.message || response.errors ? JSON.stringify(response.errors) : 'Erreur lors de la mise à jour du profil' 
        })
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error)
      let errorMessage = 'Erreur lors de la mise à jour du profil'
      if (error.response?.data) {
        if (error.response.data.errors) {
          errorMessage = `Erreurs de validation: ${JSON.stringify(error.response.data.errors)}`
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      setMessage({ type: 'error', text: errorMessage })
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    try {
      setMessage({ type: '', text: '' })

      if (passwordForm.new_password !== passwordForm.confirm_password) {
        setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' })
        return
      }

      if (passwordForm.new_password.length < 8) {
        setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères' })
        return
      }

      const response = await changePassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      })
      if (response.success) {
        setMessage({ type: 'success', text: 'Mot de passe changé avec succès' })
        setPasswordForm({
          old_password: '',
          new_password: '',
          confirm_password: ''
        })
      } else {
        // Sans cette branche, un refus du serveur ne produisait aucun retour :
        // le formulaire semblait ne rien faire.
        setMessage({
          type: 'error',
          text: response.message || 'Erreur lors du changement de mot de passe'
        })
      }
    } catch (error) {
      // La raison du refus est dans la réponse HTTP, comme pour la mise à jour
      // du profil. La lire évite d'afficher « Erreur… » là où le serveur dit
      // précisément « Mot de passe actuel incorrect ».
      setMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          error.message ||
          'Erreur lors du changement de mot de passe'
      })
    }
  }

  const compressImage = (file, maxWidth = 400, maxHeight = 400, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        const img = new Image()
        img.src = event.target?.result
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height

          // Calculer les nouvelles dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width
              width = maxWidth
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height
              height = maxHeight
            }
          }

          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(blob)
              } else {
                reject(new Error('Erreur lors de la compression'))
              }
            },
            'image/jpeg',
            quality
          )
        }
        img.onerror = reject
      }
      reader.onerror = reject
    })
  }

  const handleImageUpload = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    const file = e.target?.files?.[0]
    if (!file) {
      // Si aucun fichier, essayer de récupérer depuis l'input directement
      const input = document.getElementById('avatar-upload-input')
      if (input && input.files && input.files[0]) {
        const fileFromInput = input.files[0]
        await processImageFile(fileFromInput)
      }
      return
    }

    await processImageFile(file)
  }

  const processImageFile = async (file) => {
    if (!file) return

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier image' })
      return
    }

    // Vérifier la taille (max 10MB avant compression)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'L\'image ne doit pas dépasser 10MB' })
      return
    }

    try {
      // Compresser l'image
      const compressedBase64 = await compressImage(file)
      setProfileForm({ ...profileForm, avatar: compressedBase64 })
      setMessage({ type: 'success', text: 'Image chargée avec succès' })
      setTimeout(() => setMessage({ type: '', text: '' }), 3000)
    } catch (error) {
      console.error('Erreur lors de la compression:', error)
      setMessage({ type: 'error', text: 'Erreur lors du traitement de l\'image' })
    }
  }

  const handleCameraCapture = async () => {
    // Créer un input file avec capture
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment' // Utiliser la caméra arrière sur mobile
    
    input.onchange = async (e) => {
      const file = e.target?.files?.[0]
      if (!file) return

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier image' })
        return
      }

      // Vérifier la taille (max 10MB avant compression)
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'L\'image ne doit pas dépasser 10MB' })
        return
      }

      try {
        // Compresser l'image
        const compressedBase64 = await compressImage(file)
        setProfileForm({ ...profileForm, avatar: compressedBase64 })
        setMessage({ type: 'success', text: 'Photo prise avec succès' })
        setTimeout(() => setMessage({ type: '', text: '' }), 3000)
      } catch (error) {
        console.error('Erreur lors de la compression:', error)
        setMessage({ type: 'error', text: 'Erreur lors du traitement de l\'image' })
      }
    }
    
    input.click()
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <p className="text-base text-ink-muted" role="status">Chargement…</p>
      </div>
    )
  }

  const tabs = [
    { id: 'profile', label: 'Profil', icon: 'user' },
    { id: 'preferences', label: 'Préférences', icon: 'settings' },
    { id: 'password', label: 'Mot de passe', icon: 'lock' },
    { id: 'stats', label: 'Statistiques', icon: 'chart' },
  ]

  const devise = parametres?.devise || 'EUR'
  const inputClass = 'input'

  /** Réinitialisation des données : irréversible, donc annoncée en détail. */
  const handlePurgeData = async () => {
    const ok = await confirm({
      title: 'Réinitialiser toutes vos données ?',
      description:
        'Vos opérations seront supprimées et les soldes de vos comptes remis à zéro. Votre profil, vos paramètres et vos catégories sont conservés.',
      danger: true,
      confirmLabel: 'Réinitialiser',
      details: [
        { label: 'Opérations supprimées', value: String(stats?.operations_count ?? '—') },
        { label: 'Comptes remis à zéro', value: 'Tous' },
        { label: 'Profil et paramètres', value: 'Conservés' },
      ],
      warning: 'Cette action est irréversible : les opérations supprimées ne peuvent pas être récupérées.',
    })
    if (!ok) return

    setPurgingData(true)
    setMessage({ type: '', text: '' })
    try {
      const result = await purgeUserData()
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'Données réinitialisées.' })
        window.dispatchEvent(new Event('paramsChanged'))
        window.dispatchEvent(new Event('currencyChanged'))
        await loadProfile(true)
      } else {
        setMessage({ type: 'error', text: result.message || 'Erreur lors de la réinitialisation' })
      }
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error)
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la réinitialisation des données.',
      })
    } finally {
      setPurgingData(false)
    }
  }

  const handleCloseAccount = async () => {
    const ok = await confirm({
      title: 'Fermer votre compte ?',
      description:
        'Votre accès sera désactivé. Vos données sont conservées mais deviennent inaccessibles tant qu’un administrateur n’a pas réactivé le compte.',
      danger: true,
      confirmLabel: 'Fermer mon compte',
      details: [
        { label: 'Compte', value: profile?.username || user?.username || '—' },
        { label: 'Données', value: 'Conservées, inaccessibles' },
        { label: 'Réversible', value: 'Par un administrateur uniquement' },
      ],
    })
    if (!ok) return

    setClosingAccount(true)
    try {
      const result = await closeAccount()
      if (result.success) {
        // alert() bloquait la page juste avant une redirection : le toast
        // reste visible pendant la déconnexion.
        toast.success('Votre compte a été fermé. Vous allez être déconnecté.')
        await logout()
        navigate('/login', { replace: true })
      } else {
        setMessage({ type: 'error', text: result.message || 'Erreur lors de la fermeture du compte' })
      }
    } catch (error) {
      console.error('Erreur lors de la fermeture du compte:', error)
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la fermeture du compte',
      })
    } finally {
      setClosingAccount(false)
    }
  }

  return (
    <div className="app-page max-w-4xl">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-title">Mon profil</h1>
          <p className="app-page-subtitle">Vos informations, vos préférences et votre sécurité.</p>
        </div>
      </div>

      {/* Message de notification */}
      {message.text && (
        <div className={message.type === 'success' ? 'callout-positive' : 'callout-negative'} role="status">
          <Icon
            name={message.type === 'success' ? 'checkCircle' : 'alertCircle'}
            size={18}
            className="mt-px shrink-0"
          />
          <span className="flex-1">{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage({ type: '', text: '' })}
            className="shrink-0 rounded-sm p-0.5 opacity-70 hover:opacity-100"
            aria-label="Fermer le message"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      )}

      {/* Onglets : une seule barre, défilante sur mobile, au lieu d'un menu
          déroulant dupliquant les mêmes entrées. */}
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex w-max min-w-full gap-0.5 border-b border-line">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-base font-medium transition-colors duration-fast ${
                activeTab === tab.id
                  ? 'border-accent text-ink'
                  : 'border-transparent text-ink-secondary hover:text-ink'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============================== Profil ============================== */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <section className="card">
            <div className="card-header">
              <h2 className="card-title">Compte</h2>
              <span className={profile?.role === 'admin' ? 'badge-accent' : 'badge-neutral'}>
                <span className="dot" aria-hidden="true" />
                {profile?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
              </span>
            </div>
            <div className="px-5 py-1">
              <div className="data-row">
                <span className="data-label">Membre depuis</span>
                <span className="data-value font-normal">
                  {profile?.created_at
                    ? new Date(profile.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '—'}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">Dernière connexion</span>
                <span className="data-value font-normal">
                  {profile?.last_login ? new Date(profile.last_login).toLocaleString('fr-FR') : 'Jamais'}
                </span>
              </div>
              <div className="data-row">
                <span className="data-label">Adresse e-mail</span>
                <span className="data-value font-normal">
                  <span className={profile?.email_verified ? 'badge-positive' : 'badge-warning'}>
                    <span className="dot" aria-hidden="true" />
                    {profile?.email_verified ? 'Vérifiée' : 'Non vérifiée'}
                  </span>
                </span>
              </div>
            </div>
          </section>

          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <section className="card">
              <div className="card-header">
                <h2 className="card-title">Photo et identifiants</h2>
              </div>
              <div className="space-y-5 p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <Avatar
                    avatar={profileForm.avatar}
                    username={profileForm.username}
                    prenom={profileForm.prenom}
                    size="xl"
                  />
                  <div className="flex flex-wrap gap-2">
                    <label htmlFor="avatar-upload-input" className="btn-secondary cursor-pointer">
                      <Icon name="download" size={15} className="rotate-180" />
                      Importer une photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        onClick={(e) => {
                          // Réinitialiser la valeur pour permettre de sélectionner le même fichier
                          e.target.value = ''
                        }}
                        className="hidden"
                        id="avatar-upload-input"
                      />
                    </label>
                    <button type="button" onClick={handleCameraCapture} className="btn-secondary">
                      <Icon name="card" size={15} />
                      Prendre une photo
                    </button>
                    {profileForm.avatar && profileForm.avatar.startsWith('data:image') && (
                      <button
                        type="button"
                        onClick={() => setProfileForm({ ...profileForm, avatar: '' })}
                        className="btn-danger-ghost"
                      >
                        <Icon name="trash" size={15} />
                        Retirer
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="field">
                    <label htmlFor="username" className="field-label">Nom d’utilisateur</label>
                    <input
                      type="text"
                      id="username"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                      className={inputClass}
                      required
                      minLength={3}
                      maxLength={50}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="email" className="field-label">Adresse e-mail</label>
                    <input
                      type="email"
                      id="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2 className="card-title">Informations personnelles</h2>
              </div>
              <div className="grid gap-4 p-5 sm:grid-cols-2">
                <div className="field">
                  <label htmlFor="prenom" className="field-label">Prénom</label>
                  <input
                    type="text"
                    id="prenom"
                    value={profileForm.prenom}
                    onChange={(e) => setProfileForm({ ...profileForm, prenom: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="field">
                  <label htmlFor="nom" className="field-label">Nom</label>
                  <input
                    type="text"
                    id="nom"
                    value={profileForm.nom}
                    onChange={(e) => setProfileForm({ ...profileForm, nom: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="field">
                  <label htmlFor="telephone" className="field-label">Téléphone</label>
                  <input
                    type="tel"
                    id="telephone"
                    value={profileForm.telephone}
                    onChange={(e) => setProfileForm({ ...profileForm, telephone: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="field">
                  <label htmlFor="date_naissance" className="field-label">Date de naissance</label>
                  <input
                    type="date"
                    id="date_naissance"
                    value={profileForm.date_naissance}
                    onChange={(e) => setProfileForm({ ...profileForm, date_naissance: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
            </section>

            <section className="card">
              <div className="card-header">
                <h2 className="card-title">Adresse</h2>
              </div>
              <div className="space-y-4 p-5">
                <div className="field">
                  <label htmlFor="adresse_rue" className="field-label">Rue</label>
                  <input
                    type="text"
                    id="adresse_rue"
                    value={profileForm.adresse_rue}
                    onChange={(e) => setProfileForm({ ...profileForm, adresse_rue: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="field">
                    <label htmlFor="adresse_ville" className="field-label">Ville</label>
                    <input
                      type="text"
                      id="adresse_ville"
                      value={profileForm.adresse_ville}
                      onChange={(e) => setProfileForm({ ...profileForm, adresse_ville: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="adresse_code_postal" className="field-label">Code postal</label>
                    <input
                      type="text"
                      id="adresse_code_postal"
                      value={profileForm.adresse_code_postal}
                      onChange={(e) => setProfileForm({ ...profileForm, adresse_code_postal: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="adresse_pays" className="field-label">Pays</label>
                    <input
                      type="text"
                      id="adresse_pays"
                      value={profileForm.adresse_pays}
                      onChange={(e) => setProfileForm({ ...profileForm, adresse_pays: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="aerobase" className="field-label">Aérobase</label>
                  <input
                    type="text"
                    id="aerobase"
                    value={profileForm.aerobase}
                    onChange={(e) => setProfileForm({ ...profileForm, aerobase: e.target.value })}
                    className={inputClass}
                    placeholder="LFPO, LFPG…"
                  />
                  <p className="field-hint">Code OACI de votre aérobase principale.</p>
                </div>
              </div>
              <div className="card-footer flex justify-end">
                <button type="submit" className="btn-primary">Enregistrer</button>
              </div>
            </section>
          </form>

          {/* Zone de danger : isolée en bas, avec un contour rouge mais des
              boutons discrets — c'est la confirmation qui porte le poids. */}
          <section className="rounded-lg border border-negative-border bg-surface" aria-label="Zone de danger">
            <div className="border-b border-negative-border px-5 py-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-negative">
                <Icon name="alert" size={18} />
                Zone de danger
              </h2>
            </div>

            <div className="divide-y divide-line">
              <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-base font-medium text-ink">Réinitialiser mes données</h3>
                  <p className="mt-1 text-base text-ink-secondary">
                    Supprime toutes vos opérations et remet les soldes de vos comptes à zéro. Profil,
                    paramètres et catégories conservés.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handlePurgeData}
                  disabled={purgingData}
                  className="btn-secondary shrink-0"
                >
                  {purgingData && <Icon name="refresh" size={15} className="animate-spin" />}
                  {purgingData ? 'Réinitialisation…' : 'Réinitialiser'}
                </button>
              </div>

              <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-base font-medium text-ink">Fermer mon compte</h3>
                  <p className="mt-1 text-base text-ink-secondary">
                    Désactive votre accès. Vos données sont conservées ; seul un administrateur peut
                    réactiver le compte.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseAccount}
                  disabled={closingAccount}
                  className="btn-danger shrink-0"
                >
                  {closingAccount && <Icon name="refresh" size={15} className="animate-spin" />}
                  {closingAccount ? 'Fermeture…' : 'Fermer mon compte'}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ============================ Préférences ============================ */}
      {activeTab === 'preferences' && (
        <form onSubmit={handleProfileUpdate} className="space-y-6">
          <section className="card">
            <div className="card-header">
              <h2 className="card-title">Langue et localisation</h2>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div className="field">
                <label htmlFor="langue" className="field-label">Langue</label>
                <select
                  id="langue"
                  value={profileForm.langue}
                  onChange={(e) => setProfileForm({ ...profileForm, langue: e.target.value })}
                  className="select"
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="fuseau_horaire" className="field-label">Fuseau horaire</label>
                <input
                  type="text"
                  id="fuseau_horaire"
                  value={profileForm.fuseau_horaire}
                  onChange={(e) => setProfileForm({ ...profileForm, fuseau_horaire: e.target.value })}
                  placeholder="Europe/Paris"
                  className={inputClass}
                />
                <p className="field-hint">Europe/Paris, America/New_York, Asia/Tokyo…</p>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="card-title">Formats d’affichage</h2>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div className="field">
                <label htmlFor="format_date" className="field-label">Format de date</label>
                <select
                  id="format_date"
                  value={profileForm.format_date}
                  onChange={(e) => setProfileForm({ ...profileForm, format_date: e.target.value })}
                  className="select"
                >
                  <option value="DD/MM/YYYY">JJ/MM/AAAA</option>
                  <option value="MM/DD/YYYY">MM/JJ/AAAA</option>
                  <option value="YYYY-MM-DD">AAAA-MM-JJ</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="format_heure" className="field-label">Format d’heure</label>
                <select
                  id="format_heure"
                  value={profileForm.format_heure}
                  onChange={(e) => setProfileForm({ ...profileForm, format_heure: e.target.value })}
                  className="select"
                >
                  <option value="24h">24 heures</option>
                  <option value="12h">12 heures (AM/PM)</option>
                </select>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="card-title">Notifications</h2>
            </div>
            <div className="divide-y divide-line px-5">
              {[
                { key: 'notifications_email', label: 'Notifications par e-mail', hint: 'Résumés et messages importants.' },
                { key: 'notifications_push', label: 'Notifications push', hint: 'Sur les appareils où l’application est installée.' },
                { key: 'notifications_alertes', label: 'Alertes financières', hint: 'Lorsqu’un seuil est franchi.' },
              ].map((item) => (
                <label
                  key={item.key}
                  htmlFor={item.key}
                  className="flex cursor-pointer items-start gap-3 py-4"
                >
                  <input
                    type="checkbox"
                    id={item.key}
                    checked={profileForm[item.key]}
                    onChange={(e) => setProfileForm({ ...profileForm, [item.key]: e.target.checked })}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-xs border-line-strong text-accent focus:ring-2 focus:ring-[color:var(--ring-accent)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-base font-medium text-ink">{item.label}</span>
                    <span className="block text-sm text-ink-muted">{item.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="card-title">Apparence</h2>
            </div>
            <div className="p-5">
              <div className="field">
                <label htmlFor="theme_prefere" className="field-label">Thème</label>
                <select
                  id="theme_prefere"
                  value={profileForm.theme_prefere}
                  onChange={(e) => {
                    const value = e.target.value
                    setProfileForm({ ...profileForm, theme_prefere: value })
                    // Ce réglage était enregistré mais jamais appliqué : rien
                    // ne le lisait au démarrage. Il pilote désormais le thème.
                    setTheme(value === 'auto' ? 'system' : value)
                  }}
                  className="select sm:max-w-xs"
                >
                  <option value="auto">Automatique (selon le système)</option>
                  <option value="light">Clair</option>
                  <option value="dark">Sombre</option>
                </select>
                <p className="field-hint">Appliqué immédiatement.</p>
              </div>
            </div>
          </section>

          <div className="flex justify-end">
            <button type="submit" className="btn-primary">Enregistrer les préférences</button>
          </div>
        </form>
      )}

      {/* =========================== Mot de passe =========================== */}
      {activeTab === 'password' && (
        <section className="card">
          <div className="card-header">
            <h2 className="card-title">Changer le mot de passe</h2>
          </div>
          <form onSubmit={handlePasswordChange}>
            <div className="max-w-md space-y-4 p-5">
              <div className="field">
                <label htmlFor="old_password" className="field-label">Mot de passe actuel</label>
                <input
                  type="password"
                  id="old_password"
                  autoComplete="current-password"
                  value={passwordForm.old_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="new_password" className="field-label">Nouveau mot de passe</label>
                <input
                  type="password"
                  id="new_password"
                  autoComplete="new-password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className={inputClass}
                  required
                  minLength={8}
                />
                <p className="field-hint">
                  8 caractères minimum, avec une majuscule, une minuscule, un chiffre et un
                  caractère spécial.
                </p>
              </div>
              <div className="field">
                <label htmlFor="confirm_password" className="field-label">Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  id="confirm_password"
                  autoComplete="new-password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className={inputClass}
                  required
                  minLength={8}
                />
              </div>
            </div>
            <div className="card-footer flex justify-end">
              <button type="submit" className="btn-primary">Changer le mot de passe</button>
            </div>
          </form>
        </section>
      )}

      {/* =========================== Statistiques =========================== */}
      {activeTab === 'stats' && (
        <section aria-label="Statistiques">
          {stats ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
              <div className="card p-5">
                <h3 className="overline">Opérations</h3>
                <p data-numeric className="mt-2 text-2xl font-semibold text-ink">
                  {stats.operations_count || 0}
                </p>
              </div>
              <div className="card p-5">
                <h3 className="overline">Total des entrées</h3>
                {/* Le montant était figé en euros, quelle que soit la devise choisie. */}
                <Amount
                  value={stats.total_entrees || 0}
                  devise={devise}
                  tone="positive"
                  className="mt-2 block text-2xl font-semibold"
                />
              </div>
              <div className="card p-5">
                <h3 className="overline">Total des dépenses</h3>
                <Amount
                  value={stats.total_depenses || 0}
                  devise={devise}
                  tone="negative"
                  className="mt-2 block text-2xl font-semibold"
                />
              </div>
              <div className="card p-5">
                <h3 className="overline">Alertes</h3>
                <p data-numeric className="mt-2 text-2xl font-semibold text-ink">
                  {stats.alertes_count || 0}
                </p>
              </div>
              <div className="card p-5">
                <h3 className="overline">Dernière opération</h3>
                <p className="mt-2 text-lg font-medium text-ink">
                  {stats.last_operation_date
                    ? new Date(stats.last_operation_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'Aucune'}
                </p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="empty-state">
                <span className="empty-state-icon"><Icon name="chart" size={20} /></span>
                <p className="empty-state-title">Statistiques indisponibles</p>
                <p className="empty-state-text">Elles apparaîtront dès que vous aurez saisi des opérations.</p>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
